import type { ChatBlock } from '../slices/chat/chatSlice';

/**
 * mcgraw-hill: inline quote cards. The backend's `quote_reference` tool shows
 * a verified passage as a `document_excerpt` block and hands the model a
 * marker, `[[quote:<blockId>]]`, to put on its own line where the card goes.
 * The answer is split at the markers so each card renders in its place; the
 * markers themselves never show.
 */
export type McGrawHillAnswerSegment =
  { kind: 'text'; text: string } | { kind: 'quote'; blockId: string };

const MARKER = /\[\[quote:([^\]\s]+)\]\]/g;

/** A marker the stream has only partly delivered, at the very end. */
const PARTIAL_MARKER_AT_END =
  /\[\[(?:q(?:u(?:o(?:t(?:e(?::[^\]\s]*\]?)?)?)?)?)?)?$/;

/**
 * A card an answer places with a marker: a quoted chat-document excerpt, or
 * a reference figure.
 */
export const isMcGrawHillQuoteBlock = (block: ChatBlock): boolean =>
  (block.kind === 'document_excerpt' &&
    block.payload.source === 'chat_document') ||
  block.kind === 'mcgraw_hill_figure' ||
  block.kind === 'mcgraw_hill_actions';

/** The answer with every marker (and a half-streamed one) taken out. */
export const stripQuoteMarkers = (text: string): string =>
  text
    .replace(PARTIAL_MARKER_AT_END, '')
    .replace(/\n?[ \t]*\[\[quote:[^\]\s]+\]\][ \t]*\n?/g, '\n')
    .replace(MARKER, '');

/**
 * The answer as text and quote cards, in order. A marker whose card is not
 * among `placeable` (not this message's, not a quote, or not loaded) is
 * dropped; a half-streamed marker at the end is held back until it is whole.
 * With `placeable` null (an answer still streaming) every marker keeps its
 * place, so a card that lands late cannot merge, then split, the text around
 * it.
 */
export const splitQuoteMarkers = (
  text: string,
  placeable: ReadonlySet<string> | null
): McGrawHillAnswerSegment[] => {
  const complete = text.replace(PARTIAL_MARKER_AT_END, '');
  const segments: McGrawHillAnswerSegment[] = [];
  let cursor = 0;

  const pushText = (value: string) => {
    const last = segments[segments.length - 1];
    if (last?.kind === 'text') last.text += value;
    else if (value) segments.push({ kind: 'text', text: value });
  };

  for (const match of complete.matchAll(MARKER)) {
    pushText(complete.slice(cursor, match.index));
    const blockId = match[1];
    if (!placeable || placeable.has(blockId)) {
      segments.push({ kind: 'quote', blockId });
    }
    cursor = (match.index ?? 0) + match[0].length;
  }

  pushText(complete.slice(cursor));

  return segments
    .map((segment) =>
      segment.kind === 'text'
        ? { ...segment, text: segment.text.replace(/^\n+|\n+$/g, '') }
        : segment
    )
    .filter((segment) => segment.kind === 'quote' || segment.text.length > 0);
};

/** Where a McGraw Hill answer's blocks go. */
export interface McGrawHillAnswerLayout {
  /** The answer as text and cards, in reading order. */
  segments: McGrawHillAnswerSegment[];
  /** Every other block, for the message's usual slot. */
  slotBlocks: ChatBlock[];
  /** Cards no marker placed, under the finished answer. */
  trailingBlocks: ChatBlock[];
}

/**
 * The answer laid out in reading order, so it builds top to bottom. A card
 * shows only once the stream reaches its marker: one that lands first (a live
 * `quote_reference` arrives before the model writes its marker) waits rather
 * than showing above the text and moving down. A card no marker places goes
 * under the answer once it is written, never above it.
 */
export const layoutMcGrawHillAnswer = (
  text: string,
  blocks: readonly ChatBlock[],
  isStreaming: boolean
): McGrawHillAnswerLayout => {
  const cards = blocks.filter(isMcGrawHillQuoteBlock);
  const cardIds = new Set(cards.map((block) => block.id));
  const segments = splitQuoteMarkers(text, isStreaming ? null : cardIds);

  const placed = new Set(
    segments.flatMap((segment) =>
      segment.kind === 'quote' ? [segment.blockId] : []
    )
  );

  return {
    segments,
    slotBlocks: blocks.filter((block) => !cardIds.has(block.id)),
    trailingBlocks: isStreaming
      ? []
      : cards.filter((block) => !placed.has(block.id)),
  };
};
